import React, { useState } from 'react';
import styled from 'styled-components';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

const SpeechContainer = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f5f6fa;
`;

const Button = styled.button`
  background-color: ${props => props.isListening ? '#ef5350' : '#4834d4'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  
  &:hover {
    background-color: ${props => props.isListening ? '#d32f2f' : '#3a2bb3'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const MicIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.isListening ? '#f44336' : 'white'};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.isListening ? 'white' : '#4834d4'};
    animation: ${props => props.isListening ? 'pulse 1.5s infinite' : 'none'};
  }

  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0.7;
    }
    100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
  }
`;

const TranscriptText = styled.p`
  margin: 1rem 0;
  padding: 0.5rem;
  background-color: white;
  border-radius: 4px;
  min-height: 2rem;
  font-size: 0.9rem;
`;

const StatusText = styled.div`
  font-size: 0.8rem;
  color: ${props => props.color || '#666'};
  margin-top: 0.5rem;
`;

const ErrorText = styled.div`
  color: #f44336;
  font-size: 0.8rem;
  margin-top: 0.5rem;
`;

const SpeechToTask = ({ onAddTask }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [taskProcessed, setTaskProcessed] = useState(false);
  const [processedTask, setProcessedTask] = useState('');

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  const startListening = () => {
    resetTranscript();
    setError(null);
    setTaskProcessed(false);
    setProcessedTask('');
    SpeechRecognition.startListening({ continuous: true });
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
    
    if (transcript.trim() && !taskProcessed) {
      setProcessing(true);
      
      try {
        // Store the original transcript for color detection
        const originalTranscript = transcript;
        
        // Process the transcript directly in the frontend
        // Extract a task from the transcript - simple processing
        let taskText = transcript.trim();
        
        // Log for debugging
        console.log('Processing transcript:', taskText);
        
        // Remove common speech-to-text prefixes/phrases
        const prefixesToRemove = [
          'create a task for',
          'create a task to',
          'create task for',
          'create task to',
          'add a task for',
          'add a task to',
          'add task for',
          'add task to',
          'make a task for',
          'make a task to',
          'new task for',
          'new task to',
          'create a',
          'add a',
          'i need to',
          'we need to'
        ];
        
        // Clean up the task text
        for (const prefix of prefixesToRemove) {
          if (taskText.toLowerCase().startsWith(prefix)) {
            taskText = taskText.substring(prefix.length).trim();
            console.log(`Removed prefix "${prefix}", new text:`, taskText);
            break;
          }
        }
        
        // Capitalize the first letter if it's not already
        if (taskText.length > 0) {
          taskText = taskText.charAt(0).toUpperCase() + taskText.slice(1);
        }
        
        // Store the processed task for display
        setProcessedTask(taskText);
        console.log('Final processed task text:', taskText);
        
        // Directly call the parent's onAddTask function with the processed text
        // Handle any promise or callback if returned
        console.log('Calling onAddTask with:', taskText, originalTranscript);
        const result = onAddTask(taskText, originalTranscript);
        
        if (result instanceof Promise) {
          console.log('onAddTask returned a Promise, waiting for resolution');
          result
            .then((data) => {
              console.log('Task added successfully, response:', data);
              setTaskProcessed(true);
            })
            .catch(err => {
              console.error("Error in task addition:", err);
              setError(`Failed to add task: ${err.message || "Unknown error"}`);
            });
        } else {
          // If it's not a promise, assume success
          console.log('onAddTask completed synchronously');
          setTaskProcessed(true);
        }
      } catch (err) {
        console.error('Error processing task:', err);
        setError(`Error processing task: ${err.message || "Unknown error"}. Please try again.`);
      } finally {
        setProcessing(false);
      }
    }
  };

  if (!browserSupportsSpeechRecognition) {
    return (
      <SpeechContainer>
        <ErrorText>
          Speech recognition is not supported in this browser.
          Try using Chrome, Edge, or Safari.
        </ErrorText>
      </SpeechContainer>
    );
  }

  if (!isMicrophoneAvailable) {
    return (
      <SpeechContainer>
        <ErrorText>
          Microphone access is required. Please allow microphone access in your browser settings.
        </ErrorText>
      </SpeechContainer>
    );
  }

  return (
    <SpeechContainer>
      <h3>Add Task by Voice</h3>
      
      <div>
        {!listening ? (
          <Button onClick={startListening} disabled={processing}>
            <MicIcon isListening={false} /> Start Recording
          </Button>
        ) : (
          <Button isListening onClick={stopListening} disabled={processing}>
            <MicIcon isListening={true} /> Stop Recording
          </Button>
        )}
        
        {listening && (
          <Button onClick={resetTranscript} style={{ backgroundColor: '#757575' }}>
            Reset
          </Button>
        )}
      </div>
      
      {transcript && (
        <>
          <h4>Transcript:</h4>
          <TranscriptText>{transcript}</TranscriptText>
        </>
      )}
      
      {processing && <StatusText color="#4834d4">Processing your task...</StatusText>}
      
      {processedTask && taskProcessed && (
        <>
          <h4>Task Added:</h4>
          <TranscriptText style={{ backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
            {processedTask}
          </TranscriptText>
          <StatusText color="#4caf50">Task added successfully!</StatusText>
        </>
      )}
      
      {error && <ErrorText>{error}</ErrorText>}
      
      <StatusText>
        {listening ? 'Listening...' : 'Not listening'} 
        {listening && <span> - Speak clearly into your microphone</span>}
      </StatusText>
      
      <StatusText>
        Tip: Say something like "Create a task for updating the website" or 
        "Create a <strong>blue</strong> task for scheduling a meeting with the design team"
      </StatusText>
    </SpeechContainer>
  );
};

export default SpeechToTask; 